import type { CurrencyType } from "@repo/database";
import type { AreaMeasurementUnit } from "../types";
import { getCountryAdapter } from "./adapter";

const COUNTRY_DISPLAY_NAMES: Record<string, string> = {
  us: "the United States",
  ca: "Canada",
  nz: "New Zealand",
};

export function getCountryDisplayName(
  countryCode: string,
): string | undefined {
  return COUNTRY_DISPLAY_NAMES[countryCode.toLowerCase()];
}

export function getCurrencyForCountry(
  countryCode: string,
): CurrencyType | undefined {
  try {
    const adapter = getCountryAdapter(countryCode);
    return adapter.currency;
  } catch {
    return undefined;
  }
}

/**
 * Returns a currency only when all provided country codes resolve to one
 * country. Campaigns use this to prevent a shared intake answer from mixing
 * monetary units across target markets.
 */
export function getCurrencyForSingleCountry(
  countryCodes: readonly string[],
): CurrencyType | undefined {
  const countries = new Set(
    countryCodes
      .map((countryCode) => countryCode.trim().toLowerCase())
      .filter(Boolean),
  );
  if (countries.size !== 1) return undefined;
  const country = [...countries][0];
  return country ? getCurrencyForCountry(country) : undefined;
}

export function getAreaMeasurementUnitForCountry(
  countryCode: string,
): AreaMeasurementUnit | undefined {
  try {
    const adapter = getCountryAdapter(countryCode);
    return adapter.areaMeasurementUnit;
  } catch {
    return undefined;
  }
}

export function getAreaTranslationKey(
  baseKey: string,
  unit: AreaMeasurementUnit | undefined,
): string {
  return unit === "metric" ? `${baseKey}Metric` : baseKey;
}

export function getPostcodeFromLocationName(
  countryCode: string,
  locationName: string | undefined,
): string | undefined {
  if (!locationName) {
    return undefined;
  }

  try {
    const adapter = getCountryAdapter(countryCode);
    return adapter.isZipCode(locationName) ? locationName : undefined;
  } catch {
    return undefined;
  }
}

export function getCompactCurrencyPrefix(currency: string): string {
  switch (currency) {
    case "CAD":
      return "CA";
    case "NZD":
      return "NZ";
    default:
      return "";
  }
}
